import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { validateText } from "../../../../domain/influencerService";
import { countWords } from "../../../../util/text";
import s from "./ServiceSelector.module.css";

/*
 * ServiceInstance — one selected service, with its text field and a way to
 * remove it.
 *
 * Validation runs on blur rather than on every keystroke so the customer is
 * not told they are wrong while still typing, and clears as soon as they
 * start correcting it.
 */
export default function ServiceInstance({
  instance,
  serviceLabel,
  removeLabel,
  onTextChange,
  onRemove,
}) {
  const [error, setError] = useState("");

  const handleBlur = useCallback(() => {
    const { valid, error: message } = validateText(
      instance.customerText,
      instance,
    );
    setError(valid ? "" : message);
  }, [instance]);

  const handleChange = useCallback(
    (event) => {
      onTextChange(instance.id, event.target.value);
      if (error) setError("");
    },
    [instance.id, onTextChange, error],
  );

  const textareaId = `service-text-${instance.id}`;
  const errorId = `service-error-${instance.id}`;
  const wordCountId = `service-wc-${instance.id}`;
  const currentWords = countWords(instance.customerText ?? "");

  const describedBy =
    [error ? errorId : null, instance.maxWordCount ? wordCountId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={s.instanceEntry}>
      {instance.requiresTextInput && (
        <div className={s.textField}>
          {/* Named for the service it belongs to — several instances can be on
              screen at once, and "Your text" alone would not distinguish them. */}
          <label htmlFor={textareaId} className={s.textLabel}>
            Your text for {serviceLabel}
          </label>
          <textarea
            id={textareaId}
            className={`${s.textarea} ${error ? s.textareaError : ""}`}
            value={instance.customerText ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Type your personalized text here..."
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            rows={2}
            maxLength={1000}
          />
          <div className={s.textFooter}>
            {error && (
              <span id={errorId} className={s.errorText} role="alert">
                {error}
              </span>
            )}
            {Boolean(instance.maxWordCount) && (
              // No aria-live: this updates on every keystroke, and announcing
              // each one would drown out everything else. It is still exposed
              // through aria-describedby when the field takes focus.
              <span id={wordCountId} className={s.wordCount}>
                {currentWords} / {instance.maxWordCount} words
              </span>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className={s.removeBtn}
        onClick={() => onRemove(instance.id)}
        aria-label={removeLabel}
      >
        Remove
      </button>
    </div>
  );
}

ServiceInstance.propTypes = {
  instance: PropTypes.shape({
    id: PropTypes.string.isRequired,
    customerText: PropTypes.string,
    maxWordCount: PropTypes.number,
    requiresTextInput: PropTypes.bool,
  }).isRequired,
  serviceLabel: PropTypes.string.isRequired,
  removeLabel: PropTypes.string.isRequired,
  onTextChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};
